import { useEffect, useRef } from 'react';
import { useMatches } from 'react-router-dom';
import { useAppConfig } from '../config';
import { useReportClientEvent } from './ClientEventContext';
import { RENDER_FAILURE_EMITTED_ROUTES, renderFailureErrorName, telemetryRouteFromMatches, } from './renderFailureReporter';
import { RENDER_FAILURE_EVENT_TYPE } from './clientTelemetryContract';
import { createRouteViewSender } from './routeViewSender';
import { createUmamiSendWindow } from './umamiSendWindow';
/**
 * ADR-141 D1.1 — the mount for the route-view sender. Renders nothing.
 *
 * ⭐ ARMED BY THE PRESENCE OF CONFIG, never by a shell flag. `config.analytics`
 * is optional and only web-ui sets it, so every other shell mounts this and gets
 * a no-op. That is deliberate and it is the CSP story: the mini-app, desktop and
 * mobile shells have no `connect-src` for the collector, and a sender that were
 * armed-by-default there would produce blocked requests on every navigation —
 * invisible in JS (a CSP refusal rejects the promise the sender already
 * swallows) and loud in the browser console. Arming by config presence means the
 * shell that has not declared a collector cannot send to one. ⛔ Do not add a
 * default collector URL to make another shell "just work"; that shell needs a
 * CSP change first, which is a separate decision with a separate owner.
 *
 * ⛔ ROUTE TOKEN, not pathname — read from the matched route DEFINITION's
 * `handle.telemetryRoute`, the same source the render-failure producer uses
 * (web!515 round 4, F7). The pathname is user-controlled text; a token is a
 * closed vocabulary. An unmatched or unlabelled route yields `undefined` and
 * sends nothing, which is the right answer: undeclared means omitted.
 */
export function RouteViewReporter() {
    const config = useAppConfig();
    const report = useReportClientEvent();
    const matches = useMatches();
    const route = telemetryRouteFromMatches(matches);
    // ⚠️ A REF BUILT LAZILY IN AN EFFECT, not a `useMemo`, and the difference is
    // load-bearing. The sender OWNS the same-token memo and the ceiling window, so
    // re-creating it resets both — the same hazard `ApiProvider` documents for the
    // client-event budget. React may discard a `useMemo` at will (documented), and
    // StrictMode's dev double-mount is exactly when it would: a discarded sender
    // forgets it just sent, and the effect fires again with the same token,
    // producing the duplicate Umami row that breaks D2's 1+1 join. A ref survives,
    // so the 2s same-token window absorbs the second effect run instead of a fresh
    // sender being handed a clean slate.
    //
    // `liveRef` is the same mechanism for the opposite reason: the sender is built
    // ONCE and its ceiling callback fires much later, so closing over the BUILD
    // render's `report`/`route` would pin every route-loop report to whichever
    // screen the session started on.
    //
    // ⛔ EVERY ref ACCESS here is inside an effect, deliberately. Writing
    // `ref.current = x` during render is the obvious shape and `react-hooks`
    // rejects it ("Cannot access refs during render") — a render may be discarded
    // and re-run, so a render-phase write is not guaranteed to correspond to
    // anything. Building the sender in the send effect rather than during render
    // is what makes that rule satisfiable without a disable.
    const senderRef = useRef(null);
    const liveRef = useRef({ config, report, route });
    // Declared FIRST on purpose: effects run in declaration order, so the values
    // the ceiling callback reads are current before the send effect below can
    // trip it.
    useEffect(() => {
        liveRef.current = { config, report, route };
    });
    useEffect(() => {
        if (route === undefined)
            return;
        senderRef.current ??= buildRouteViewSender(liveRef.current.config, liveRef);
        senderRef.current(route);
    }, [route]);
    return null;
}
const NO_OP = () => { };
/**
 * Builds the sender, or a no-op — it NEVER throws.
 *
 * ⛔ The guard is not defensive padding; it is the difference between losing
 * telemetry and losing the SCREEN. `RouteViewReporter` renders inside the
 * router, so a throw during its render is caught by the router's own
 * ErrorBoundary and replaced with the default error UI: the whole surface goes
 * down because an analytics helper could not be built. `buildUrl` throws on a
 * malformed `collectorUrl`, which is one bad env var away.
 *
 * ⚠️ FOUND BY A MUTANT THAT SURVIVED, which is worth recording because the way
 * it survived is the interesting part. Forcing the unconfigured branch to build
 * a sender threw `TypeError: Cannot read properties of undefined` on every
 * render — and all seven tests passed, because the router swallowed the throw
 * into its error boundary and the assertions were all about what did NOT reach
 * the collector, which is exactly what a crashed component achieves. A suite
 * that mounts its subject inside a router is blind to that subject crashing
 * unless it asserts otherwise; the reporter's suite now asserts it explicitly.
 */
function buildRouteViewSender(config, live) {
    const analytics = config.analytics;
    // ⚠️ THIS LINE SURVIVES MUTATION, KNOWINGLY. Deleting it changes no observable
    // behaviour: `analytics.collectorUrl` then throws inside the `try` below and
    // the catch returns the same no-op. Recorded rather than left for the next
    // reader to rediscover, because the usual rule is that a surviving mutant is a
    // missing test — here it is not, and I could not write one, so the honest
    // options were delete it or justify it.
    //
    // It is kept because the two guards cover the same case for OPPOSITE reasons.
    // An unconfigured shell is the EXPECTED state of five of the six shells that
    // mount this — routing that through an exception makes the normal path a
    // thrown TypeError, which is wrong on its own terms and hides the one signal
    // (something reached the catch) that means a real misconfiguration. The catch
    // exists for the unexpected: a malformed URL, a future field that throws.
    // ⇒ If the `try` is ever narrowed, or these reads move out of it, this line
    // becomes load-bearing again — which is the other reason not to delete it.
    if (analytics === undefined)
        return NO_OP;
    try {
        return createRouteViewSender({
            apiMode: config.apiMode,
            collectorUrl: analytics.collectorUrl,
            websiteId: analytics.websiteId,
            clientShell: config.clientShell,
            authMode: analytics.authMode,
            hostname: analytics.hostname,
            // Built here, so it lives exactly as long as the sender does — this function is called
            // once per mount behind `senderRef.current ??=`, which is the same once-only protection
            // the sender gets and is what a census needs (see `ApiProvider`'s note on why a discarded
            // window is worse than a refilled bucket).
            //
            // ⚠️ `live.current.report` is read AT EMIT TIME, not at build time — the same reason the
            // ceiling callback below does. A window can close much later than the render that built
            // it, and closing over the build render's reporter would pin every summary to whichever
            // screen the session started on.
            umamiWindow: createUmamiSendWindow({ report: (event) => live.current.report(event) }),
            onCeilingTripped: () => {
                // A route loop IS a bounded technical error, so it belongs on the
                // client-events path where the existing budget bounds it — not on the
                // Umami plane it just stopped writing to.
                //
                // ⭐ A REAL `RangeError` is constructed rather than the string
                // 'RangeError' being asserted. The contract states that
                // `render_failure` names are native error BRANDS read from the value's
                // own prototype; passing a literal would make the emitted brand a claim
                // about a value that does not exist. Building the error means
                // `renderFailureErrorName` derives it the same way it derives every
                // other one, and the name stays true by construction.
                const error = new RangeError('route-view ceiling tripped');
                const bounded = RENDER_FAILURE_EMITTED_ROUTES.find((t) => t === live.current.route);
                live.current.report({
                    eventType: RENDER_FAILURE_EVENT_TYPE,
                    errorName: renderFailureErrorName(error),
                    ...(bounded !== undefined ? { context: { route: bounded } } : {}),
                });
            },
        });
    }
    catch {
        return NO_OP;
    }
}
//# sourceMappingURL=RouteViewReporter.js.map